import React, { useContext, useState } from 'react';
import { AppContext } from '~/providers/AppProvider';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { SingleSelect, Textarea, InputText } from '@boatsetter-backup/js-component-lib';
import { getBudgetOptions } from './config';
import { postContactForm } from '~/api/services/bookings';
import FormSuccessfullySent from '~/components/contact/formSuccessfullySent';
import FormSentFailed from '~/components/contact/formSentFailed';
import * as Sentry from '@sentry/nextjs';
import { Trans, withI18n } from '@lingui/react';
import { t } from '@lingui/macro';
import LoadingOverlay from '~/components/loadingOverlay';

import dynamic from 'next/dynamic';
import type { EmptyBookingModel } from '~/api/client';
import { getCookie } from '~/utils/metarouter';
const DateInput = dynamic(async () => await import('~/components/contact/dateInput'));

interface Props {
  fwRef: any;
  formSent: boolean;
  setFormSent: any;
  i18n: any;
}

const ContactForm = ({ fwRef, formSent, setFormSent, i18n }: Props) => {
  const appCtx = useContext(AppContext);
  const currency = appCtx.state.currency;
  const [successfullySent, setSuccessfullySent] = useState(false);
  const [dateError, setDateError] = useState(false);
  const [isSendingFrom, setIsSendingForm] = useState(false);

  const { register, handleSubmit, watch, errors, setValue } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const onSubmit = async (data) => {
    if (data.dates === undefined || data.dates.length < 1) {
      setDateError(true);
      return;
    }

    setIsSendingForm(true);
    let postDataRes = null;

    const gaClientId =
      window.ga && window.ga?.getAll ? window.ga?.getAll()[0]?.get('clientId') : getCookie('_ga')?.slice(6);
    // @ts-expect-error
    const rudderstackEnabled = typeof window?.rudderanalytics?.getAnonymousId() === 'string';

    const paramsFromForm: { emptyBookingModel: EmptyBookingModel } = {
      emptyBookingModel: {
        Name: data.name,
        Email: data.email.trim(),
        Phone: data.phone,
        Destination: data.destination,
        Passengers: data.passengers ? data.passengers : undefined,
        Dates: data.dates !== undefined ? data.dates : [],
        AdditionalDetails: data.details,
        Budget: data.budget?.value,
        GoogleAnalyticsClientId: gaClientId,
        // @ts-expect-error
        AnonymousId: rudderstackEnabled ? window?.rudderanalytics?.getAnonymousId() : undefined,
      },
    };

    await postContactForm(paramsFromForm)
      .then((response) => {
        postDataRes = response.data;
        setSuccessfullySent(true);
        setFormSent(true);
      })
      .catch((e) => {
        console.error(e);
        setSuccessfullySent(false);
        setFormSent(true);
        Sentry.setExtra('error', e);
        Sentry.captureMessage('Contact form API request error');
      });
    setIsSendingForm(false);
  };

  return (
    <LoadingOverlay show={isSendingFrom}>
      <Wrapper ref={fwRef}>
        {formSent ? (
          successfullySent ? (
            <FormSuccessfullySent />
          ) : (
            <FormSentFailed setFormSent={setFormSent} />
          )
        ) : (
          <>
            <Heading>
              <Trans id="contact.form.heading" />
            </Heading>
            <form onSubmit={handleSubmit(onSubmit)}>
              <NameWrapper>
                <InputText
                  bold
                  disabled={false}
                  errorMessage={'Please provide at least 4 chars'}
                  error={errors.name}
                  hasError={errors.name}
                  min={4}
                  label={<Trans id="contact.form.name" />}
                  type="text"
                  name="name"
                  hasValue={watch('name')}
                  inputRef={register({
                    required: `${i18n._(t`errorMessage.phoneRequired`)}`,
                    minLength: 4,
                  })}
                  required
                />
              </NameWrapper>

              <EmailWrapper>
                <InputText
                  bold
                  disabled={false}
                  errorMessage={'Please provide at least 4 chars'}
                  error={errors.email}
                  hasError={errors.email}
                  min={4}
                  label={<Trans id="contact.form.email" />}
                  type="text"
                  name="email"
                  hasValue={watch('email')}
                  inputRef={register({
                    required: `${i18n._(t`errorMessage.emailRequired`)}`,
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,20}$/i,
                      message: `${i18n._(t`errorMessage.invalidEmail`)}`,
                    },
                  })}
                  required
                />
              </EmailWrapper>

              <DateInput
                dateError={dateError}
                inputRef={register('dates')}
                setDateValue={(dateValue) => {
                  setValue('dates', dateValue);
                  if (dateValue.length > 0 && dateValue.length !== undefined) {
                    setDateError(false);
                  }
                }}
              />

              <InputWrapper>
                <InputText
                  disabled={false}
                  error={errors.phone}
                  hasError={errors.phone}
                  min={4}
                  label={<Trans id="contact.form.phoneNumber" />}
                  type="phone"
                  name="phone"
                  hasValue={watch('phone')}
                  inputRef={register({
                    pattern: {
                      value: /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/i,
                      message: `${i18n._(t`errorMessage.invalidPhone`)}`,
                    },
                  })}
                />
              </InputWrapper>

              <NameWrapper>
                <InputText
                  disabled={false}
                  error={errors.destination}
                  hasError={errors.destination}
                  label={<Trans id="contact.form.destination" />}
                  type="text"
                  required
                  name="destination"
                  hasValue={watch('destination')}
                  inputRef={register({
                    required: `Destination is required`,
                  })}
                />
              </NameWrapper>

              <InputWrapper>
                <InputText
                  disabled={false}
                  error={errors.passengers}
                  hasError={errors.passengers}
                  label={<Trans id="contact.form.passengers" />}
                  type="number"
                  name="passengers"
                  hasValue={watch('passengers')}
                  inputRef={register({ min: 1 })}
                />
              </InputWrapper>

              <BudgetInputWrapper>
                <SingleSelect
                  instanceId={'budget_select'}
                  options={getBudgetOptions(currency)}
                  placeholder={<Trans id="contact.form.budget" />}
                  onChange={(budgetObj) => {
                    setValue('budget', budgetObj);
                  }}
                  inputRef={register('budget')}
                />
              </BudgetInputWrapper>

              <AdditionalDetailsWrapper>
                <Textarea
                  bold
                  disabled={false}
                  error={errors.details}
                  hasError={errors.details}
                  label={<Trans id="contact.form.additionalDetails" />}
                  placeholder=""
                  type="text"
                  name="details"
                  hasValue={watch('details')}
                  inputRef={register()}
                />
              </AdditionalDetailsWrapper>

              <SubmitButton disabled={!!isSendingFrom} type="submit">
                <Trans id="contact.sendInquiry" />
              </SubmitButton>
            </form>
            <Terms>
              <Trans id="contact.form.policies" />
            </Terms>
          </>
        )}
      </Wrapper>
    </LoadingOverlay>
  );
};

export default withI18n()(ContactForm);

const Wrapper = styled.div`
  background: white;
  padding: 40px 50px;
  border-radius: 10px;
  position: relative;
  z-index: 1;
  max-width: 700px;
  width: 100%;

  @media screen and (max-width: 640px) {
    padding: 20px;
  }
`;

const Heading = styled.div`
  margin: 0;
  font-weight: 600;
  color: #17233c;
  flex-shrink: 1;
  font-size: 1.25em;
  line-height: 1.4;
  margin-bottom: 1em;
`;

const Terms = styled.div`
  font-weight: 400;
  box-sizing: inherit;
  margin: 0;
  font-size: 0.875em;
  line-height: 1.714285;
  color: #8e9697 !important;
  margin-bottom: 0;
`;

const SubmitButton = styled.button`
  border: none;
  text-align: center;
  padding: 1em;
  font-size: 16px;
  border-radius: 5px;
  background-color: #102a5e;
  color: #fff;
  text-transform: uppercase;
  width: 100%;
  margin-top: 1em;
  margin-bottom: 1em;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    pointer-events: none;
  }
`;

const InputWrapper = styled.div`
  div[class*='InputText_InputTextBottomSpacing'] {
    margin-bottom: 22px;
  }
`;

const NameWrapper = styled(InputWrapper)`
  label:after {
    content: ' *';
    color: #bc350a;
  }
`;

const AdditionalDetailsWrapper = styled(InputWrapper)`
  margin-top: 22px;

  textarea {
    height: 200px;
    font-size: 16px;
    font-family: 'AvenirNext', 'Avenir', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  }
  label {
    color: hsl(0, 0%, 50%);
  }
`;

const BudgetInputWrapper = styled(InputWrapper)`
  div[class*='control'] {
    height: 50px;
    border: solid 1px #dbdfe5;
  }

  div[class*='singleValue'] {
    font-weight: 700;
  }

  div[class*='menu'] {
    z-index: 2;
  }
`;

const EmailWrapper = styled(NameWrapper)``;
